import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Phone, Shield, CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import { API } from '../config/api';

export default function PhoneVerificationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, refreshUser } = useAuth();
  
  const [step, setStep] = useState('phone'); // 'phone' | 'otp' | 'success'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [maskedPhone, setMaskedPhone] = useState('');
  
  const otpRefs = useRef([]);
  const returnTo = searchParams.get('returnTo') || '/dashboard';

  // If user already has verified phone, redirect
  useEffect(() => {
    if (user?.phone_verified) {
      navigate(returnTo);
    }
    if (user?.phone) {
      setMaskedPhone(user.phone);
    }
  }, [user, navigate, returnTo]);

  // Countdown for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const formatPhoneDisplay = (value) => {
    // Remove non-digits
    const digits = value.replace(/\D/g, '');
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneDisplay(e.target.value);
    setPhoneNumber(formatted);
  };

  const handleSendOtp = async () => {
    const digits = phoneNumber.replace(/\D/g, '');
    if (digits.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/phone/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ phone_number: digits })
      });

      // Use arrayBuffer to avoid body stream issues
      let data;
      try {
        const buffer = await response.arrayBuffer();
        const text = new TextDecoder().decode(buffer);
        data = JSON.parse(text);
      } catch (parseError) {
        throw new Error('Failed to process response');
      }
      
      if (response.ok && data.success) {
        setMaskedPhone(data.phone_masked);
        setResendCooldown(data.resend_cooldown_seconds || 60);
        setStep('otp');
        toast.success('Verification code sent!');
      } else {
        toast.error(data.detail || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otpCode];
    newOtp[index] = value;
    setOtpCode(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === 5 && newOtp.every(d => d)) {
      handleVerifyOtp(newOtp.join(''));
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setOtpCode(newOtp);
      handleVerifyOtp(pastedData);
    }
  };

  const handleVerifyOtp = async (code) => {
    const digits = phoneNumber.replace(/\D/g, '');
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/phone/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          phone_number: digits,
          code: code,
          user_id: user?.user_id
        })
      });

      // Use arrayBuffer to avoid body stream issues
      let data;
      try {
        const buffer = await response.arrayBuffer();
        const text = new TextDecoder().decode(buffer);
        data = JSON.parse(text);
      } catch (parseError) {
        throw new Error('Failed to process response');
      }
      
      if (data.valid) {
        setStep('success');
        toast.success('Phone number verified!');
        
        // Refresh user data
        if (refreshUser) {
          await refreshUser();
        }
        
        // Redirect after short delay
        setTimeout(() => {
          navigate(returnTo);
        }, 2000);
      } else {
        toast.error(data.message || 'Invalid verification code');
        setOtpCode(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    await handleSendOtp();
  };

  // Success state
  if (step === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card className="glass-card border-border p-8 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
            
            <h1 className="text-2xl font-bold mb-3">Phone Verified!</h1>
            <p className="text-muted-foreground mb-6">
              Your phone number has been successfully verified. Redirecting you now...
            </p>
            
            <Loader2 className="w-6 h-6 animate-spin text-secondary mx-auto" />
          </Card>
        </div>
      </div>
    );
  }

  // OTP entry state
  if (step === 'otp') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-secondary" />
            </div>
            <h1 className="font-heading text-3xl font-bold mb-2">Enter Verification Code</h1>
            <p className="text-muted-foreground">
              We sent a 6-digit code to <span className="text-foreground">{maskedPhone}</span>
            </p>
          </div>

          <Card className="glass-card border-border p-6">
            <div className="space-y-6">
              {/* OTP Input */}
              <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
                {otpCode.map((digit, index) => (
                  <Input
                    key={index}
                    ref={el => otpRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="w-12 h-14 text-center text-xl font-bold"
                    data-testid={`otp-input-${index}`}
                    disabled={loading}
                  />
                ))}
              </div>

              {/* Verify Button */}
              <Button
                onClick={() => handleVerifyOtp(otpCode.join(''))}
                className="w-full bg-secondary hover:bg-secondary/90"
                disabled={loading || otpCode.some(d => !d)}
                data-testid="verify-otp-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Code'
                )}
              </Button>

              {/* Resend */}
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Didn't receive the code?</p>
                <Button
                  variant="link"
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0}
                  className="text-secondary"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                </Button>
              </div>

              {/* Change Number */}
              <div className="pt-4 border-t border-border text-center">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setStep('phone');
                    setOtpCode(['', '', '', '', '', '']);
                  }}
                  className="text-sm"
                >
                  Use a different phone number
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Phone entry state
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Phone className="w-8 h-8 text-secondary" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-2">Verify Your Phone</h1>
          <p className="text-muted-foreground">
            {user?.phone_verification_required 
              ? "Phone verification is required for your account type"
              : "Add your phone number for extra security"}
          </p>
        </div>

        <Card className="glass-card border-border p-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(667) 910-1233"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  className="pl-10"
                  maxLength={14}
                  data-testid="phone-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                We'll send a verification code via SMS
              </p>
            </div>

            <Button
              onClick={handleSendOtp}
              className="w-full bg-secondary hover:bg-secondary/90"
              disabled={loading || phoneNumber.replace(/\D/g, '').length !== 10}
              data-testid="send-otp-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  Send Verification Code
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>

            <div className="pt-4 border-t border-border">
              <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
                <Shield className="w-3 h-3" />
                Your number is secure and won't be shared
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
